import React from 'react';
import { Upload, X, AlertCircle } from 'lucide-react';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { AppConfirmDialog } from '../../../components/AppConfirmDialog';
import { isTenderDocumentPdf, TenderDocumentPdfModal } from './TenderDocumentPdfModal';
import {
  GAEB90_UNSUPPORTED_MESSAGE,
  isGaeb90Filename,
  type TenderDocument,
} from '../types';

interface TenderDropzoneProps {
  ausschreibungId: string;
  documents: TenderDocument[];
  onUploaded: (doc: TenderDocument) => void;
  onDeleted: (docId: string) => void;
}

const ALLOWED_ACCEPT =
  'application/pdf,image/png,image/jpeg,image/webp,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.pdf,.doc,.docx,.xls,.xlsx,.csv,.png,.jpg,.jpeg,.webp,.x80,.x81,.x82,.x83,.x84,.x85,.x86,.xml,.txt';

export const TenderDropzone: React.FC<TenderDropzoneProps> = ({
  ausschreibungId,
  documents,
  onUploaded,
  onDeleted,
}) => {
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = React.useState(false);
  const [uploading, setUploading] = React.useState<string[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = React.useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = React.useState(false);
  const [previewDoc, setPreviewDoc] = React.useState<TenderDocument | null>(null);

  const uploadFiles = React.useCallback(
    async (fileList: FileList | File[]) => {
      const files = Array.from(fileList);
      if (files.length === 0) return;
      setError(null);
      for (const file of files) {
        if (isGaeb90Filename(file.name)) {
          setError(GAEB90_UNSUPPORTED_MESSAGE);
          continue;
        }
        const tag = `${file.name}_${file.size}`;
        setUploading((prev) => [...prev, tag]);
        try {
          const fd = new FormData();
          fd.append('file', file, file.name);
          const res = await fetch(
            `/api/ausschreibungen/${encodeURIComponent(ausschreibungId)}/documents`,
            { method: 'POST', body: fd, credentials: 'include' },
          );
          const body = (await res.json().catch(() => ({}))) as
            | TenderDocument
            | { error?: string };
          if (!res.ok) {
            throw new Error((body as { error?: string }).error || 'Upload fehlgeschlagen.');
          }
          const uploaded = body as TenderDocument;
          if (uploaded.duplicate) {
            setError(`„${file.name}“ ist bereits in der Akte (gleicher Inhalt).`);
            continue;
          }
          onUploaded(uploaded);
        } catch (err) {
          setError(err instanceof Error ? err.message : String(err));
        } finally {
          setUploading((prev) => prev.filter((x) => x !== tag));
        }
      }
    },
    [ausschreibungId, onUploaded],
  );

  const pendingDoc = pendingDeleteId
    ? documents.find((d) => d.id === pendingDeleteId)
    : null;

  const executeDeleteDocument = React.useCallback(async () => {
    if (!pendingDeleteId) return;
    setDeleteLoading(true);
    try {
      const res = await fetch(
        `/api/ausschreibungen/${encodeURIComponent(ausschreibungId)}/documents/${encodeURIComponent(pendingDeleteId)}`,
        { method: 'DELETE', credentials: 'include' },
      );
      if (res.ok) {
        onDeleted(pendingDeleteId);
      } else {
        setError('Dokument konnte nicht gelöscht werden.');
      }
      setPendingDeleteId(null);
    } finally {
      setDeleteLoading(false);
    }
  }, [ausschreibungId, onDeleted, pendingDeleteId]);

  /** Gruppen statt Badges: GAEB-LV, Lieferantenangebote, Sonstige. Leere Gruppen bleiben weg. */
  const groups = React.useMemo(() => {
    const order: Array<{ kind: TenderDocument['doc_kind']; label: string }> = [
      { kind: 'gaeb', label: 'GAEB-LV' },
      { kind: 'supplier_quote', label: 'Lieferantenangebote' },
      { kind: 'other', label: 'Sonstige Dokumente' },
    ];
    return order
      .map((g) => ({ ...g, docs: documents.filter((d) => (d.doc_kind ?? 'other') === g.kind) }))
      .filter((g) => g.docs.length > 0);
  }, [documents]);

  return (
    <div className="h-full min-h-0 w-full min-w-0 flex flex-col gap-2">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (e.dataTransfer.files?.length) void uploadFiles(e.dataTransfer.files);
        }}
        onClick={() => inputRef.current?.click()}
        className={`w-full flex flex-col items-center justify-center gap-1.5 rounded-lg border-2 border-dashed px-4 py-5 cursor-pointer transition-colors ${
          dragOver
            ? 'border-pipedrive-blue bg-blue-50'
            : 'border-app-border hover:border-app-border-strong bg-app-surface-muted'
        }`}
      >
        <Upload className="w-6 h-6 text-slate-400" />
        <div className="text-sm font-medium text-app-text-muted">
          Dokumente hierher ziehen oder klicken
        </div>
        <div className="text-sm text-app-text-subtle">
          PDF, Word, Excel, Bilder, GAEB DA XML (.x83) · jeweils max. 50 MB
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={ALLOWED_ACCEPT}
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files?.length) void uploadFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {(documents.length > 0 || uploading.length > 0) && (
        <ul className="mt-3 min-h-0 flex-1 w-full min-w-0 flex flex-col gap-4 overflow-y-auto overscroll-contain">
          {uploading.map((tag) => (
            <li
              key={tag}
              className="flex w-full min-w-0 items-center gap-2 rounded-md px-2 py-1.5 text-sm text-app-text-muted"
            >
              <LoadingSpinner className="text-app-text-subtle" />
              <span className="flex-1 truncate">{tag.split('_').slice(0, -1).join('_')}</span>
              <span className="text-sm text-slate-400">wird hochgeladen…</span>
            </li>
          ))}
          {groups.map((g) => (
            <li key={g.kind} className="min-w-0">
              <div className="px-2 pb-1 text-sm font-medium text-app-text-subtle">
                {g.label}
                <span className="ml-1 font-normal">· {g.docs.length}</span>
              </div>
              <ul>
          {g.docs.map((doc) => (
            <li
              key={doc.id}
              role={isTenderDocumentPdf(doc) ? 'button' : undefined}
              tabIndex={isTenderDocumentPdf(doc) ? 0 : undefined}
              onClick={() => {
                if (isTenderDocumentPdf(doc)) setPreviewDoc(doc);
              }}
              onKeyDown={(e) => {
                if (!isTenderDocumentPdf(doc)) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setPreviewDoc(doc);
                }
              }}
              className={`group flex w-full min-w-0 items-center gap-2 rounded-md px-2 py-1.5 text-sm ${
                isTenderDocumentPdf(doc) ? 'cursor-pointer hover:bg-app-hover transition-colors' : ''
              }`}
              title={isTenderDocumentPdf(doc) ? 'PDF in Vorschau öffnen' : undefined}
            >
              <span className="flex-1 min-w-0 truncate text-sm text-app-text">{doc.filename}</span>
              <span className="ml-auto flex h-5 w-14 shrink-0 items-center justify-end">
              {doc.page_count != null && doc.page_count > 0 && (
                <span className="text-sm text-slate-400 tabular-nums group-hover:hidden group-focus-within:hidden">{doc.page_count} S.</span>
              )}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setPendingDeleteId(doc.id);
                }}
                className="hidden group-hover:inline-flex group-focus-within:inline-flex h-5 w-5 items-center justify-center rounded text-slate-400 hover:text-red-600 transition-colors"
                aria-label="Löschen"
                title="Dokument löschen"
              >
                <X className="w-4 h-4" />
              </button>
              </span>
            </li>
          ))}
              </ul>
            </li>
          ))}
        </ul>
      )}

      <TenderDocumentPdfModal
        ausschreibungId={ausschreibungId}
        document={previewDoc}
        onClose={() => setPreviewDoc(null)}
      />

      <AppConfirmDialog
        open={pendingDeleteId != null}
        onClose={() => !deleteLoading && setPendingDeleteId(null)}
        title="Dokument löschen?"
        description={
          pendingDoc ? (
            <>
              <span className="font-medium text-app-text">{pendingDoc.filename}</span> wird aus
              dieser Ausschreibung entfernt.
            </>
          ) : null
        }
        confirmLabel="Löschen"
        cancelLabel="Abbrechen"
        destructive
        isLoading={deleteLoading}
        onConfirm={executeDeleteDocument}
      />
    </div>
  );
};
